import { Autocomplete, Box, MenuItem, TextField } from '@mui/material';
import { useId } from 'react';

import { FormField, isDefined } from 'common';

import { FlowDefinitionStateTag } from '../../../../../../Definitions/common/StateTag.tsx';
import { useFlowDefinitionContext } from '../../../../../../context.ts';
import { useReactFlowViewContext } from '../../../../../context/context.tsx';
import { useSubFlowFieldsContext } from '../context.ts';

interface FlowDefinitionAutocompleteProps {
  helperText: string;
  label: string;
}

/**
 * Must be wrapped in a SubFlowFieldsProvider
 */
export function FlowDefinitionAutocomplete(props: FlowDefinitionAutocompleteProps) {
  const { label, helperText } = props;

  const { selectedSubflowAndFlowDefinitions, specificVersionFlowDefinitionIdFieldProps } =
    useSubFlowFieldsContext();

  const {
    value: flowDefinitionId,
    visibleError: visibleFlowDefinitionIdError,
    setValue: setFlowDefinitionId,
  } = specificVersionFlowDefinitionIdFieldProps;

  const { isEditable } = useReactFlowViewContext();
  const { flowDefinition } = useFlowDefinitionContext();
  const availableFlowDefinitions = selectedSubflowAndFlowDefinitions?.flowDefinitions
    .filter((definition) => {
      return definition.id !== flowDefinition.id;
    })
    ?.sort((a, b) => b.version - a.version);

  const selectedFlowDefinition = availableFlowDefinitions?.find(
    (flowDefinition) => flowDefinition.id === flowDefinitionId
  );
  const fieldId = useId();

  return (
    <>
      <FormField
        id={fieldId}
        label={label}
        error={visibleFlowDefinitionIdError}
        helperText={helperText}
      >
        <Autocomplete
          id={fieldId}
          options={availableFlowDefinitions ?? []}
          loading={!isDefined(selectedSubflowAndFlowDefinitions)}
          disabled={!isEditable}
          value={selectedFlowDefinition ?? null}
          onChange={(_, newFlowDefinition) => {
            if (newFlowDefinition) {
              setFlowDefinitionId(newFlowDefinition.id);
            } else {
              setFlowDefinitionId(undefined);
            }
          }}
          renderInput={(params) => <TextField {...params} size="small" />}
          getOptionLabel={(option) => `${option.name} (v${option.version} ${option.state})`}
          renderOption={(listItemProps, option) => (
            <MenuItem {...listItemProps} key={option.id}>
              <Box display="flex" justifyContent="space-between" width="100%">
                <span>{`${option.name} v${option.version}`}</span>
                <span>
                  <FlowDefinitionStateTag state={option.state} />
                </span>
              </Box>
            </MenuItem>
          )}
        />
      </FormField>
    </>
  );
}
